import { Link } from 'react-router'
import type { JSX, ReactNode } from 'react'
import { Lockup } from '@/shell/Lockup'
import { ThemeToggle } from '@/components/ThemeToggle'

/**
 * The frame for every page reachable without a session.
 *
 * One column, centred, no shell — the reader may be on a phone, in a different
 * browser, or in a mail client's preview pane, and none of those has an
 * organisation selected for a sidebar to describe.
 *
 * The theme toggle is here as well as in the shell because "light and dark both
 * work, everywhere" (CLAUDE.md) includes the screens someone lands on from an
 * email, which for many people is the first AugRank screen they ever see.
 */
export function PublicPage({
  title,
  children,
  footer,
}: {
  title: string
  children: ReactNode
  footer?: ReactNode
}): JSX.Element {
  return (
    <div className="app-public">
      <div className="ar-row" style={{ width: 'min(28rem, 100%)' }}>
        <Link className="app-public__lockup" to="/">
          <Lockup />
        </Link>
        <span className="app-spacer" />
        <ThemeToggle />
      </div>

      {/*
        The title sits **above** the card, not in a header row inside it.

        `h1`, not `h3`: this is the page's own title — there is nothing above it on
        any of the twelve public screens — and it read `h3` until 2026-07-30, so
        every one of them started its heading hierarchy at level 3 with no `h1`
        anywhere in the document.

        That fix moved the semantics and deliberately left the visual alone, noting
        at the time that S-D2 goes further. It does: `design/screens/auth.html:155`
        puts the `h1` in a `.sc-auth__head` block *before* `.ar-card`, and
        `ar-card__header` does not appear once in the whole auth mockup — all
        eleven screens there are titled above the card, centred, at
        `--ar-text-h1-size`. §I V5 is that gap, on the product's most-seen screen.

        Closing it here rather than per screen because the deviation was never in a
        screen — it was in this component, identically on all twelve.
      */}
      <div className="app-public__head">
        <h1 className="app-public__title">{title}</h1>
      </div>

      <section className="ar-card app-public__card">{children}</section>

      {footer ? <div className="app-public__foot">{footer}</div> : null}
    </div>
  )
}
